import { Component } from "react";

import { classNames } from "@helpers/css";

import { SkillItem } from "./skill-commands-extension";

type Props = {
  items: SkillItem[];
  command: (props: SkillItem) => void;
};

type State = {
  selectedIndex: number;
};

const SECTION_ORDER: Array<{
  label: string;
  visibility: SkillItem["visibility"];
}> = [
  { label: "Your skills", visibility: "PERSONAL" },
  { label: "Workspace skills", visibility: "WORKSPACE" },
  { label: "Platform skills", visibility: "PLATFORM" },
];

const VISIBILITY_BADGE: Record<
  SkillItem["visibility"],
  { label: string; className: string }
> = {
  PERSONAL: {
    label: "Personal",
    className: "bg-emerald-100 text-emerald-700",
  },
  WORKSPACE: {
    label: "Workspace",
    className: "bg-blue-100 text-blue-700",
  },
  PLATFORM: {
    label: "Platform",
    className: "bg-purple-200 text-purple-700",
  },
};

export default class SkillCommandsList extends Component<Props, State> {
  state: State = {
    selectedIndex: 0,
  };

  /**
   * Items in the same order they're rendered (Personal → Workspace → Platform).
   * `selectedIndex` indexes into this list — NOT `this.props.items`, which may
   * arrive in any order from the server.
   */
  getDisplayItems(): SkillItem[] {
    return SECTION_ORDER.flatMap((section) =>
      this.props.items.filter((item) => item.visibility === section.visibility),
    );
  }

  componentDidUpdate(oldProps: Props) {
    if (this.props.items !== oldProps.items) {
      this.setState({ selectedIndex: 0 });
    }
  }

  onKeyDown({ event }: { event: KeyboardEvent }) {
    if (event.key === "ArrowUp") {
      this.upHandler();
      return true;
    }
    if (event.key === "ArrowDown") {
      this.downHandler();
      return true;
    }
    if (event.key === "Enter") {
      if (this.props.items.length > 0) {
        this.enterHandler();
        return true;
      }
      return false;
    }
    if (event.key === "Tab") {
      if (this.props.items.length > 0) {
        this.enterHandler();
        return true;
      }
      return false;
    }
    return false;
  }

  upHandler() {
    const newIndex = this.state.selectedIndex - 1;
    if (newIndex >= 0) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  downHandler() {
    const newIndex = this.state.selectedIndex + 1;
    if (newIndex < this.getDisplayItems().length) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  enterHandler() {
    this.selectItem(this.state.selectedIndex);
  }

  selectItem(index: number) {
    const item = this.getDisplayItems()[index];
    if (item) {
      this.props.command(item);
    }
  }

  render() {
    const { items } = this.props;
    const displayItems = this.getDisplayItems();
    if (items.length === 0) return null;

    return (
      <div
        className="relative rounded bg-white border text-sm shadow overflow-y-auto max-h-72"
        aria-label="ask-ai-skill-commands"
        role="listbox"
      >
        {SECTION_ORDER.map((section) => {
          const sectionItems = items.filter(
            (item) => item.visibility === section.visibility,
          );
          if (sectionItems.length === 0) return null;

          return (
            <div key={section.visibility}>
              <div className="sticky top-0 bg-gray-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b">
                {section.label}
              </div>
              {sectionItems.map((item) => {
                const flatIndex = displayItems.indexOf(item);
                const badge = VISIBILITY_BADGE[item.visibility];
                return (
                  <button
                    type="button"
                    role="option"
                    aria-selected={flatIndex === this.state.selectedIndex}
                    className={classNames(
                      "block w-80 text-left px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 text-sm",
                      flatIndex === this.state.selectedIndex && "bg-gray-100",
                    )}
                    key={`${item.__typename}-${item.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.selectItem(flatIndex);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs font-medium text-blue-link truncate">
                        <span aria-hidden="true">/</span>
                        <span>{item.slug}</span>
                      </span>
                      <span
                        className={classNames(
                          "shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium leading-none",
                          badge.className,
                        )}
                      >
                        {badge.label}
                      </span>
                    </div>
                    {item.description && (
                      <span className="block text-gray-400 text-xs mt-0.5">
                        {item.description}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
}
